import type { Citation, CorpusData, UsageStats, PageInfo, IssueSubmission, QuerySearchResult, UserPreferences, ChecklistData, ChecklistItem, RetrievalTree, ChecklistEvidence, ChecklistAuditEntry } from './types';
import { getAuthHeaders, handleAuthFailure } from './components/AuthGate';

const API_BASE = import.meta.env.VITE_API_URL || '';

/* ── Pending image keys side-channel ──────────────────────────── */
// QueryInput uploads images and stores keys here before calling onSend().
// streamQuery() consumes them atomically when building the POST body.
let _pendingImageKeys: string[] = [];

export function setPendingImageKeys(keys: string[]): void {
  _pendingImageKeys = [...keys];
}

function consumePendingImageKeys(): string[] {
  const keys = _pendingImageKeys;
  _pendingImageKeys = [];
  return keys;
}

/* ── Upload image to R2 for vision queries ───────────────────── */

export async function uploadImage(blob: Blob, filename?: string): Promise<string> {
  const formData = new FormData();
  formData.append('image', blob, filename || 'image.jpg');

  const res = await authFetch(`${API_BASE}/api/upload-image`, {
    method: 'POST',
    body: formData,
    // Note: do NOT set Content-Type header — browser sets it with boundary for multipart
  });

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: 'Upload failed' })) as { error?: string };
    throw new Error(errBody.error || `Upload failed (${res.status})`);
  }

  const data = await res.json() as { key: string };
  return data.key;
}

/* ── Auth-aware fetch wrapper ──────────────────────────────── */

/**
 * Wraps fetch() to inject the Bearer token from localStorage
 * and handle 401 responses by clearing auth and reloading.
 */
export async function authFetch(url: string, init?: RequestInit): Promise<Response> {
  const headers = {
    ...getAuthHeaders(),
    ...(init?.headers || {}),
  };

  const res = await fetch(url, { ...init, headers });

  if (res.status === 401) {
    handleAuthFailure();
    throw new Error('Authentication expired');
  }

  return res;
}

/* ── SSE streaming query ─────────────────────────────────────── */

/**
 * POST a query to the backend and stream the response via SSE.
 *
 * Expected SSE payload shapes:
 *   data: {"type":"text","content":"…"}
 *   data: {"type":"citations","citations":[…]}
 *   data: {"type":"error","message":"…"}
 *   data: [DONE]
 *
 * Pass an AbortSignal to cancel mid-stream (e.g. from a stop button).
 */
export interface QueryResultResponse {
  status: 'complete' | 'pending';
  response_text?: string;
  citations?: Array<{ id: string; standard: string; clause_number: string | null; provenance?: string | null }>;
  query_text?: string;
  confidence?: string | null;
  confidence_reason?: string | null;
  /** G1: Corpus version active when this answer was generated */
  corpus_version?: string | null;
  /** G1: Current corpus version (for staleness comparison) */
  current_corpus_version?: string | null;
  /** F35: retrieval map tree stored with the query, replayed on restore */
  retrieval_tree?: RetrievalTree | null;
}

export interface SessionInfo {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export async function fetchSessions(userId: string): Promise<SessionInfo[]> {
  const res = await authFetch(`${API_BASE}/api/sessions?user_id=${encodeURIComponent(userId)}`);
  if (!res.ok) return [];
  const data: { sessions: SessionInfo[] } = await res.json();
  return data.sessions ?? [];
}

export interface SessionMessage {
  id: string;
  role: string;
  content: string;
  citations_json?: string;
  confidence?: string | null;
  confidence_reason?: string | null;
  /** G1: Corpus version active when this message was generated */
  corpus_version?: string | null;
  /** F35: link to the queries row (assistant messages from 2026-07-13 on) */
  query_id?: string | null;
  /** F35: raw retrieval map JSON joined from queries — replay on session restore */
  retrieval_tree_json?: string | null;
}

export interface SessionDetail {
  messages: SessionMessage[];
  /** Map of message_id → ChecklistData (with items and server IDs) */
  checklists: Record<string, ChecklistData>;
  /** G1: Current corpus version for staleness comparison */
  current_corpus_version?: string | null;
}

export async function fetchSessionMessages(sessionId: string): Promise<SessionMessage[]> {
  const res = await authFetch(`${API_BASE}/api/sessions/${encodeURIComponent(sessionId)}`);
  if (!res.ok) return [];
  const data = await res.json() as { messages: SessionMessage[]; checklists?: Record<string, ChecklistData> };
  return data.messages ?? [];
}

/**
 * Fetch full session detail including messages AND checklists keyed by message_id.
 */
export async function fetchSessionDetail(sessionId: string): Promise<SessionDetail> {
  const res = await authFetch(`${API_BASE}/api/sessions/${encodeURIComponent(sessionId)}`);
  if (!res.ok) return { messages: [], checklists: {} };
  const data = await res.json() as { messages: SessionMessage[]; checklists?: Record<string, ChecklistData>; current_corpus_version?: string | null };
  return {
    messages: data.messages ?? [],
    checklists: data.checklists ?? {},
    current_corpus_version: data.current_corpus_version ?? null,
  };
}

export async function deleteSession(sessionId: string): Promise<void> {
  await authFetch(`${API_BASE}/api/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE' });
}

export async function streamQuery(
  query: string,
  onText: (chunk: string) => void,
  onCitations: (citations: Citation[]) => void,
  onDone: () => void,
  onError: (message: string) => void,
  signal?: AbortSignal,
  onQueryId?: (queryId: string) => void,
  onUsage?: (usage: UsageStats) => void,
  sessionId?: string | null,
  onConfidence?: (confidence: string, reason: string | null) => void,
  plainEnglish?: boolean,
  domainFilter?: string[],
  checklistMode?: boolean,
  onChecklist?: (checklist: ChecklistData) => void,
  /** G4: Called when response is served from query cache */
  onCachedHit?: () => void,
  /** Retrieval Map: called when retrieval_tree SSE event is received */
  onRetrievalTree?: (tree: RetrievalTree) => void,
  /** G5-fix: when true, sends skip_cache=true to worker so cached answer is bypassed */
  skipCache?: boolean,
): Promise<void> {
  let response: Response;

  try {
    const bodyPayload: Record<string, unknown> = {
      query,
      session_id: sessionId ?? undefined,
    };
    if (plainEnglish) bodyPayload.plain_english = true;
    if (domainFilter && domainFilter.length > 0) bodyPayload.domain_filter = domainFilter;
    if (checklistMode) bodyPayload.checklist_mode = true;
    if (skipCache) bodyPayload.skip_cache = true;
    // Pick up any pending image keys set by QueryInput before onSend
    const imageKeys = consumePendingImageKeys();
    if (imageKeys.length > 0) bodyPayload.image_keys = imageKeys;

    response = await fetch(`${API_BASE}/api/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(bodyPayload),
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      onDone(); // User cancelled — treat as clean finish
      return;
    }
    onError('Unable to reach the server. Check your connection and try again.');
    return;
  }

  if (response.status === 401) {
    handleAuthFailure();
    onError('Your session has expired. Please sign in again.');
    return;
  }

  if (!response.ok) {
    // Try to extract a meaningful error message from the response body
    try {
      const errBody = await response.json() as { error?: string; code?: string };
      if (errBody.error) {
        onError(errBody.error);
        return;
      }
    } catch { /* body not JSON — fall through */ }
    onError(`Server error (${response.status}). Please try again.`);
    return;
  }

  const body = response.body;
  if (!body) {
    onError('Empty response from server.');
    return;
  }

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // If aborted before reading starts, cancel the reader
  if (signal) {
    signal.addEventListener('abort', () => {
      reader.cancel().catch(() => {/* ignore */});
    }, { once: true });
  }

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      // If aborted mid-stream, bail out cleanly
      if (signal?.aborted) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by blank lines (\n\n)
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() ?? '';

      for (const chunk of chunks) {

        // Parse named SSE event (event: <name>\ndata: <json>)
        let eventName = 'message';
        let eventData = '';
        for (const line of chunk.split('\n')) {
          if (line.startsWith('event: ')) {
            eventName = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            eventData += line.slice(6);
          } else if (line.startsWith('data:')) {
            eventData += line.slice(5);
          }
        }

        if (!eventData) continue;
        if (eventData === '[DONE]') {
          onDone();
          return;
        }

        try {
          const parsed = JSON.parse(eventData);

          if (eventName === 'queryId') {
            if (onQueryId && parsed.queryId) onQueryId(parsed.queryId as string);
            // sessionId is also sent in this event — store it via a side channel
            if (parsed.sessionId && typeof window !== 'undefined') {
              try { localStorage.setItem('standards-session-id', parsed.sessionId as string); } catch { /* ignore */ }
            }
            // G4: Cache hit indicator
            if (parsed.cached === true && onCachedHit) onCachedHit();
            continue;
          }

          if (eventName === 'citations') {
            // Worker sends an array of citation metadata objects
            if (Array.isArray(parsed)) {
              const mapped: Citation[] = (parsed as Array<{
                index: number;
                id: string | number;
                standard: string;
                clause_number: string | null;
                table_number?: string | null;
                figure_number?: string | null;
                title: string | null;
                body?: string | null;
                skill?: string | null;
                provenance?: string | null;
              }>).map((c) => ({
                id: String(c.id),
                number: c.index,
                standard: c.standard,
                clause: c.table_number ? `Table ${c.table_number}` : c.figure_number ? `Fig. ${c.figure_number}` : (c.clause_number ?? ''),
                text: c.body ?? c.title ?? '',
                skill: c.skill ?? undefined,
                provenance: c.provenance ?? null,
              }));
              onCitations(mapped);
            }
            continue;
          }

          if (eventName === 'confidence') {
            if (onConfidence && parsed.confidence) {
              onConfidence(
                parsed.confidence as string,
                (parsed.confidence_reason as string | null) ?? null,
              );
            }
            continue;
          }

          if (eventName === 'checklist') {
            if (onChecklist && parsed.checklist) {
              const clData = parsed.checklist as ChecklistData;
              // Attach server-assigned checklist_id if present
              if (parsed.checklist_id) {
                clData.checklistId = parsed.checklist_id as string;
              }
              onChecklist(clData);
            }
            continue;
          }

          if (eventName === 'usage') {
            if (onUsage) {
              onUsage(parsed as UsageStats);
            }
            continue;
          }

          if (eventName === 'retrieval_tree') {
            if (onRetrievalTree) {
              onRetrievalTree(parsed as RetrievalTree);
            }
            continue;
          }

          if (eventName === 'text') {
            if (parsed.text) onText(parsed.text as string);
            continue;
          }

          if (eventName === 'done') {
            onDone();
            return;
          }

          // Legacy fallback: type-based events
          switch (parsed.type) {
            case 'text':
              onText(parsed.content as string);
              break;
            case 'citations':
              onCitations(parsed.citations as Citation[]);
              break;
            case 'error':
              onError(parsed.message as string);
              return;
          }
        } catch {
          // Non-JSON line — ignore
        }
      }
    }

    // Stream ended (or was aborted) — call done
    onDone();
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      onDone(); // User cancelled
    } else {
      onError('Connection lost. Please try again.');
    }
  }
}

/* ── Fetch a single clause's full text ───────────────────────── */

export async function fetchClauseText(clauseId: string, signal?: AbortSignal): Promise<string> {
  const res = await authFetch(`${API_BASE}/api/clause/${encodeURIComponent(clauseId)}`, { signal });
  if (!res.ok) throw new Error(`Failed to fetch clause (${res.status})`);
  const data = await res.json() as Record<string, unknown>;
  // Worker returns full ClauseRow — prefer enriched_text > body > text
  return (data.enriched_text ?? data.body ?? data.text ?? '') as string;
}

/* ── Fetch full clause metadata (for ghost node expansion) ──── */

export interface ClauseFull {
  id: string;
  standard: string;
  clause_number: string | null;
  table_number: string | null;
  figure_number: string | null;
  title: string;
  body: string;
  skill: string | null;
}

export async function fetchClauseFull(clauseId: string, signal?: AbortSignal): Promise<ClauseFull> {
  const res = await authFetch(`${API_BASE}/api/clause/${encodeURIComponent(clauseId)}`, { signal });
  if (!res.ok) throw new Error(`Failed to fetch clause (${res.status})`);
  const data = await res.json() as Record<string, unknown>;
  return {
    id: String(data.id ?? clauseId),
    standard: String(data.standard ?? ''),
    clause_number: (data.clause_number as string | null) ?? null,
    table_number: (data.table_number as string | null) ?? null,
    figure_number: (data.figure_number as string | null) ?? null,
    title: String(data.title ?? ''),
    body: String(data.enriched_text ?? data.body ?? ''),
    skill: (data.skill as string | null) ?? null,
  };
}

/* ── Batch-fetch multiple clauses (for React Flow multi-clause expand) ─── */

interface ExpandResponseClause {
  id: string;
  standard: string | null;
  skill: string | null;
  clause_number: string | null;
  table_number?: string | null;
  figure_number?: string | null;
  title: string | null;
  body: string | null;
  edition?: string | null;
  provenance?: string | null;
  found: boolean;
}

interface ExpandResponse {
  clauses: ExpandResponseClause[];
  requested: number;
  found: number;
  missing: string[];
}

/**
 * Batch-fetch up to 20 clause full records in one API call.
 * If more than 20 IDs are provided, they are automatically split into
 * sequential batches of 20 and the results merged.
 * Missing IDs (found:false from server) are filtered out of the return array.
 */
export async function fetchClausesBatch(
  clauseIds: string[],
  options?: { includeBody?: boolean; includeMetadata?: boolean },
): Promise<ClauseFull[]> {
  if (clauseIds.length === 0) return [];

  const BATCH_SIZE = 20;
  const { includeBody = true, includeMetadata = true } = options ?? {};

  // Split into batches of 20 if needed
  const batches: string[][] = [];
  for (let i = 0; i < clauseIds.length; i += BATCH_SIZE) {
    batches.push(clauseIds.slice(i, i + BATCH_SIZE));
  }

  const results: ClauseFull[] = [];

  for (const batch of batches) {
    const res = await authFetch(`${API_BASE}/api/query/expand`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clauseIds: batch, includeBody, includeMetadata }),
    });

    if (!res.ok) {
      console.warn(`[fetchClausesBatch] Request failed (${res.status}) for batch of ${batch.length}`);
      continue;
    }

    const data = await res.json() as ExpandResponse;

    for (const c of data.clauses) {
      if (!c.found) continue; // skip missing
      results.push({
        id: c.id,
        standard: c.standard ?? '',
        clause_number: c.clause_number ?? null,
        table_number: c.table_number ?? null,
        figure_number: c.figure_number ?? null,
        title: c.title ?? '',
        body: c.body ?? '',
        skill: c.skill ?? null,
      });
    }
  }

  return results;
}

/* ── Fetch a completed query result by queryId ──────────────────── */

export async function fetchQueryResult(queryId: string): Promise<QueryResultResponse> {
  const res = await authFetch(`${API_BASE}/api/query/${encodeURIComponent(queryId)}`);
  if (!res.ok) throw new Error(`Failed to fetch query result (${res.status})`);
  return res.json() as Promise<QueryResultResponse>;
}

/* ── Fetch corpus inventory (C1) ─────────────────────────────── */

export async function fetchCorpus(): Promise<CorpusData> {
  const res = await authFetch(`${API_BASE}/api/corpus`);
  if (!res.ok) throw new Error(`Failed to fetch corpus (${res.status})`);
  return res.json() as Promise<CorpusData>;
}

/* ── A6: live corpus counts (public, no auth) ──────────────────── */
// Single source of truth for domain/clause/standard/page-image counts.
// Falls back to build-time CORPUS_STATS at call sites if this fails.
export interface LiveCorpusStats {
  domains: number;
  clauses: number;
  standards: number;
  pdfs: number;
  pageImages: number;
  crossRefs?: number;
}

export async function fetchCorpusStats(): Promise<Partial<LiveCorpusStats> | null> {
  try {
    const res = await fetch(`${API_BASE}/api/corpus-stats`);
    if (!res.ok) return null;
    const data = (await res.json()) as Partial<LiveCorpusStats>;
    // Guard against the empty-object fallback the worker returns on error.
    if (!data || typeof data.domains !== 'number') return null;
    return data;
  } catch {
    return null;
  }
}

/* ── Phase 6: clause page images + issue reporting ──────────────── */

export async function fetchSignedPageUrl(pdfId: string, page: number): Promise<string | null> {
  try {
    const res = await authFetch(`${API_BASE}/api/page-sign/${encodeURIComponent(pdfId)}/${page}`);
    if (!res.ok) return null;
    const data = await res.json() as { url: string };
    return data.url ?? null;
  } catch { return null; }
}

export async function fetchClausePages(clauseId: string): Promise<PageInfo[]> {
  const res = await authFetch(`${API_BASE}/api/clause-pages/${encodeURIComponent(clauseId)}`);
  if (!res.ok) return [];
  const data = await res.json() as { pages: PageInfo[] };
  return data.pages ?? [];
}

export function getPageImageUrl(standardSlug: string, pageNum: number): string {
  return `${API_BASE}/api/pages/${encodeURIComponent(standardSlug)}/${pageNum}`;
}

export async function requestPageMapping(
  clauseId: string | number,
  standard?: string,
  clauseNumber?: string,
): Promise<{ ok: boolean; request_count?: number; message?: string }> {
  const res = await authFetch(`${API_BASE}/api/request-page-mapping`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clause_id: clauseId, standard, clause_number: clauseNumber }),
  });
  if (!res.ok) return { ok: false };
  return res.json() as Promise<{ ok: boolean; request_count?: number; message?: string }>;
}

export async function submitIssue(issue: IssueSubmission): Promise<{ ok: boolean; id?: string }> {
  const res = await authFetch(`${API_BASE}/api/issues`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(issue),
  });
  return res.json() as Promise<{ ok: boolean; id?: string }>;
}

/* ── G1: Export all sessions as JSON ─────────────────────────── */

export async function exportAllSessions(): Promise<void> {
  const res = await authFetch(`${API_BASE}/api/user/sessions/export`);
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `compliance-hub-sessions-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ── G5: Fetch similar query from cache ──────────────────────── */

export interface SimilarQueryResult {
  found: boolean;
  similarity?: number;
  query_text?: string;
  response_preview?: string;
  query_hash?: string;
}

export async function fetchSimilarQuery(q: string): Promise<SimilarQueryResult> {
  try {
    const res = await authFetch(`${API_BASE}/api/query/similar?q=${encodeURIComponent(q)}`);
    if (!res.ok) return { found: false };
    return await res.json() as SimilarQueryResult;
  } catch {
    return { found: false };
  }
}


/* ── Full-text query search ──────────────────────────────────── */

export async function searchQueries(
  search: string,
  limit: number = 20,
): Promise<QuerySearchResult[]> {
  const params = new URLSearchParams({ search, limit: String(limit) });
  const res = await authFetch(`${API_BASE}/api/user/queries?${params}`);
  if (!res.ok) return [];
  const data = await res.json() as { results: QuerySearchResult[] };
  return data.results ?? [];
}

/* ── Submit feedback (C4) ────────────────────────────────────── */

export async function submitFeedback(
  queryId: string,
  rating: 'up' | 'down',
  comment?: string,
): Promise<void> {
  const body: Record<string, string> = { query_id: queryId, rating };
  if (comment) body.comment = comment;
  const res = await authFetch(`${API_BASE}/api/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to submit feedback (${res.status})`);
}

/* ── Phase 8: User Preferences ────────────────────────────────── */

import { DEFAULT_PREFERENCES } from './types';

export async function fetchPreferences(): Promise<UserPreferences> {
  try {
    const res = await authFetch(`${API_BASE}/api/user/preferences`);
    if (!res.ok) return DEFAULT_PREFERENCES;
    return await res.json() as UserPreferences;
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

/* ── Edition Comparison ────────────────────────────────────── */

export interface CompareClausePair {
  clause_number: string;
  title_a: string | null;
  title_b: string | null;
  body_a: string | null;
  body_b: string | null;
  status: 'changed' | 'added' | 'removed' | 'unchanged';
}

export interface CompareResponse {
  standard_a: string;
  standard_b: string;
  topic: string | null;
  summary: {
    changed: number;
    added: number;
    removed: number;
    unchanged: number;
    total: number;
  };
  pairs: CompareClausePair[];
  ai_summary: string | null;
}

export async function fetchCompare(
  standardA: string,
  standardB: string,
  topic?: string,
): Promise<CompareResponse> {
  const params = new URLSearchParams({
    standard_a: standardA,
    standard_b: standardB,
  });
  if (topic) params.set('topic', topic);

  const res = await authFetch(`${API_BASE}/api/compare?${params}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Compare failed' })) as { error?: string };
    throw new Error(err.error || `Compare failed (${res.status})`);
  }
  return res.json() as Promise<CompareResponse>;
}

/* ── Available editions for compare ────────────────────────── */

export interface EditionInfo {
  standard: string;
  edition: string | null;
  clause_count: number;
}

export async function fetchEditions(): Promise<EditionInfo[]> {
  const corpus = await fetchCorpus();
  const editions: EditionInfo[] = [];
  for (const skill of corpus.skills) {
    for (const std of skill.standards) {
      editions.push({
        standard: std.standard,
        edition: std.edition || null,
        clause_count: std.clause_count,
      });
    }
  }
  return editions;
}

/* ── Research Brief SSE streaming ───────────────────────────── */

import type { BriefSection, Citation as BriefCitation } from './types';

export interface ResearchBriefCallbacks {
  onDecomposition: (sections: { id: number; title: string; query: string }[]) => void;
  onSectionStart: (index: number, title: string) => void;
  onSectionText: (index: number, content: string) => void;
  onSectionCitations: (index: number, citations: BriefCitation[]) => void;
  onSectionDone: (index: number) => void;
  onAssemblyStart: () => void;
  onSynthesisText: (content: string) => void;
  onDone: (briefId: string) => void;
  onError: (error: string) => void;
  onStatusUpdate?: (status: string) => void;
}

/**
 * POST to /api/research and stream the research brief via SSE.
 *
 * SSE event shapes:
 *   {"type":"decomposition","sections":[...]}
 *   {"type":"section_start","index":0,"title":"..."}
 *   {"type":"section_text","index":0,"content":"..."}
 *   {"type":"section_citations","index":0,"citations":[...]}
 *   {"type":"section_done","index":0}
 *   {"type":"assembly_start"}
 *   {"type":"text","content":"..."}
 *   {"type":"done","brief_id":"..."}
 */
export async function streamResearchBrief(
  query: string,
  callbacks: ResearchBriefCallbacks,
  signal?: AbortSignal,
  sessionId?: string | null,
  domains?: string[],
): Promise<void> {
  let response: Response;

  try {
    const bodyPayload: Record<string, unknown> = {
      topic: query,
      session_id: sessionId ?? undefined,
    };
    if (domains && domains.length > 0) bodyPayload.domain_filter = domains;

    response = await fetch(`${API_BASE}/api/research`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(bodyPayload),
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      callbacks.onDone('');
      return;
    }
    callbacks.onError('Unable to reach the server. Check your connection and try again.');
    return;
  }

  if (response.status === 401) {
    handleAuthFailure();
    callbacks.onError('Your session has expired. Please sign in again.');
    return;
  }

  if (!response.ok) {
    try {
      const errBody = await response.json() as { error?: string };
      if (errBody.error) {
        callbacks.onError(errBody.error);
        return;
      }
    } catch { /* body not JSON */ }
    callbacks.onError(`Server error (${response.status}). Please try again.`);
    return;
  }

  const body = response.body;
  if (!body) {
    callbacks.onError('Empty response from server.');
    return;
  }

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  if (signal) {
    signal.addEventListener('abort', () => {
      reader.cancel().catch(() => {});
    }, { once: true });
  }

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (signal?.aborted) break;

      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() ?? '';

      for (const chunk of chunks) {
        let eventData = '';
        let eventName = 'message';
        for (const line of chunk.split('\n')) {
          if (line.startsWith('event: ')) {
            eventName = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            eventData += line.slice(6);
          } else if (line.startsWith('data:')) {
            eventData += line.slice(5);
          }
        }

        if (!eventData || eventData === '[DONE]') {
          if (eventData === '[DONE]') {
            callbacks.onDone('');
            return;
          }
          continue;
        }

        try {
          const parsed = JSON.parse(eventData) as Record<string, unknown>;
          // Use named SSE event or fall back to parsed.type
          const eventType = eventName !== 'message' ? eventName : (parsed.type as string ?? '');

          switch (eventType) {
            case 'brief_created':
              callbacks.onStatusUpdate?.('Preparing research brief…');
              break;
            case 'decomposition_start':
              callbacks.onStatusUpdate?.('Breaking down your question…');
              break;
            case 'section_search': {
              const subQ = (parsed.sub_question as string) ?? '';
              const count = (parsed.result_count as number) ?? 0;
              callbacks.onStatusUpdate?.(`Searching: ${subQ.slice(0, 80)}${subQ.length > 80 ? '…' : ''} (${count} clauses found)`);
              break;
            }
            case 'decomposition': {
              const sections = (parsed.sections as Array<{ id: number; title: string; query: string }>)
                ?? (parsed.sub_questions as string[] ?? []).map((q: string, i: number) => ({
                  id: i,
                  title: q,
                  query: q,
                }));
              callbacks.onDecomposition(sections);
              break;
            }
            case 'section_start': {
              const sIdx = (parsed.index as number) ?? (parsed.section_index as number) ?? 0;
              const sTitle = (parsed.title as string) ?? (parsed.sub_question as string) ?? '';
              callbacks.onSectionStart(sIdx, sTitle);
              callbacks.onStatusUpdate?.(`Writing section ${sIdx + 1}: ${sTitle.slice(0, 60)}${sTitle.length > 60 ? '…' : ''}`);
              break;
            }
            case 'section_text':
              callbacks.onSectionText(
                parsed.index as number ?? parsed.section_index as number ?? 0,
                (parsed.content as string) ?? (parsed.text as string) ?? '',
              );
              break;
            case 'section_citations': {
              const rawCitations = parsed.citations as Array<{
                id?: string | number;
                index?: number;
                standard?: string;
                clause_number?: string;
                table_number?: string | null;
                figure_number?: string | null;
                title?: string;
                body?: string;
                skill?: string;
              }> ?? [];
              const mapped: BriefCitation[] = rawCitations.map((c, ci) => ({
                id: String(c.id ?? `rb-cit-${ci}`),
                number: c.index ?? ci + 1,
                standard: c.standard ?? '',
                clause: c.table_number ? `Table ${c.table_number}` : c.figure_number ? `Fig. ${c.figure_number}` : (c.clause_number ?? ''),
                text: c.body ?? c.title ?? '',
                skill: c.skill ?? undefined,
              }));
              callbacks.onSectionCitations(
                parsed.index as number ?? parsed.section_index as number ?? 0,
                mapped,
              );
              break;
            }
            case 'section_done':
            case 'section_complete':
              callbacks.onSectionDone(
                parsed.index as number ?? parsed.section_index as number ?? 0,
              );
              break;
            case 'assembly_start':
            case 'synthesis_start':
              callbacks.onAssemblyStart();
              break;
            case 'text':
            case 'synthesis_text':
              callbacks.onSynthesisText((parsed.content as string) ?? (parsed.text as string) ?? '');
              break;
            case 'done':
              callbacks.onDone((parsed.brief_id as string) ?? '');
              return;
            case 'error':
              callbacks.onError((parsed.message as string) ?? (parsed.error_message as string) ?? 'Research brief generation failed');
              return;
          }
        } catch {
          // Non-JSON line — ignore
        }
      }
    }
    callbacks.onDone('');
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      callbacks.onDone('');
    } else {
      callbacks.onError('Connection lost. Please try again.');
    }
  }
}

export async function savePreferences(prefs: UserPreferences): Promise<UserPreferences> {
  const res = await authFetch(`${API_BASE}/api/user/preferences`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(prefs),
  });
  if (!res.ok) throw new Error(`Failed to save preferences (${res.status})`);
  return await res.json() as UserPreferences;
}

/* ── Checklist Persistence API ──────────────────────────────────── */

/**
 * Save (PATCH) a single checklist item's status or notes to D1.
 * Use for immediate status changes and debounced notes updates.
 */
export async function saveChecklistItem(
  checklistId: string,
  itemId: string,
  status?: string,
  notes?: string,
): Promise<void> {
  const body: Record<string, string> = {};
  if (status !== undefined) body.status = status;
  if (notes !== undefined) body.notes = notes;

  const res = await authFetch(
    `${API_BASE}/api/checklists/${encodeURIComponent(checklistId)}/items/${encodeURIComponent(itemId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    console.warn(`[checklist] Failed to save item ${itemId}: ${res.status}`);
  }
}

/* (fetchChecklist lives below the comparison section — checklist id == query id) */

/* ── Side-by-Side Jurisdiction Comparison ────────────────── */

export interface ComparisonCallbacks {
  onComparisonStart: (comparisonId: string, jurisdictions: { id: string; label: string }[]) => void;
  onJurisdictionStart: (jurisdictionId: string, label: string) => void;
  onJurisdictionText: (jurisdictionId: string, text: string) => void;
  onJurisdictionCitations: (jurisdictionId: string, citations: Citation[]) => void;
  onJurisdictionDone: (jurisdictionId: string, confidence: string, confidenceReason: string) => void;
  onJurisdictionError: (jurisdictionId: string, error: string) => void;
  onSynthesisStart: () => void;
  onSynthesisText: (text: string) => void;
  onSynthesisDone: (confidence: string) => void;
  onUsage: (data: { inputTokens: number; outputTokens: number; cost_usd: number }) => void;
  onDone: () => void;
  onError: (error: string) => void;
}

export async function streamComparison(
  query: string,
  jurisdictions: string[],
  callbacks: ComparisonCallbacks,
  signal?: AbortSignal,
  sessionId?: string | null,
): Promise<void> {
  let response: Response;

  try {
    const bodyPayload: Record<string, unknown> = {
      query,
      jurisdictions,
      session_id: sessionId ?? undefined,
    };

    response = await fetch(`${API_BASE}/api/compare-jurisdictions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(bodyPayload),
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      callbacks.onDone();
      return;
    }
    callbacks.onError('Unable to reach the server. Check your connection and try again.');
    return;
  }

  if (response.status === 401) {
    handleAuthFailure();
    callbacks.onError('Your session has expired. Please sign in again.');
    return;
  }

  if (!response.ok) {
    try {
      const errBody = await response.json() as { error?: string };
      if (errBody.error) {
        callbacks.onError(errBody.error);
        return;
      }
    } catch { /* body not JSON */ }
    callbacks.onError(`Server error (${response.status}). Please try again.`);
    return;
  }

  const body = response.body;
  if (!body) {
    callbacks.onError('Empty response from server.');
    return;
  }

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  if (signal) {
    signal.addEventListener('abort', () => {
      reader.cancel().catch(() => {});
    }, { once: true });
  }

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (signal?.aborted) break;

      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() ?? '';

      for (const chunk of chunks) {
        let eventName = 'message';
        let eventData = '';
        for (const line of chunk.split('\n')) {
          if (line.startsWith('event: ')) eventName = line.slice(7).trim();
          else if (line.startsWith('data: ')) eventData += line.slice(6);
          else if (line.startsWith('data:')) eventData += line.slice(5);
        }

        if (!eventData) continue;

        try {
          const parsed = JSON.parse(eventData) as Record<string, unknown>;

          switch (eventName) {
            case 'comparison_start':
              callbacks.onComparisonStart(
                parsed.comparison_id as string,
                parsed.jurisdictions as { id: string; label: string }[],
              );
              break;
            case 'jurisdiction_start':
              callbacks.onJurisdictionStart(
                parsed.jurisdiction_id as string,
                parsed.label as string,
              );
              break;
            case 'jurisdiction_text':
              callbacks.onJurisdictionText(
                parsed.jurisdiction_id as string,
                parsed.text as string,
              );
              break;
            case 'jurisdiction_citations': {
              const rawCits = parsed.citations as Array<{
                index: number;
                id: string | number;
                standard: string;
                clause_number: string | null;
                table_number?: string | null;
                figure_number?: string | null;
                title: string | null;
                body?: string | null;
                skill?: string | null;
                provenance?: string | null;
              }>;
              const mapped: Citation[] = (rawCits || []).map((c) => ({
                id: String(c.id),
                number: c.index,
                standard: c.standard,
                clause: c.table_number ? `Table ${c.table_number}` : c.figure_number ? `Fig. ${c.figure_number}` : (c.clause_number ?? ''),
                text: c.body ?? c.title ?? '',
                skill: c.skill ?? undefined,
                provenance: c.provenance ?? null,
              }));
              callbacks.onJurisdictionCitations(parsed.jurisdiction_id as string, mapped);
              break;
            }
            case 'jurisdiction_done':
              callbacks.onJurisdictionDone(
                parsed.jurisdiction_id as string,
                (parsed.confidence as string) ?? 'MEDIUM',
                (parsed.confidence_reason as string) ?? '',
              );
              break;
            case 'jurisdiction_error':
              callbacks.onJurisdictionError(
                parsed.jurisdiction_id as string,
                parsed.error as string,
              );
              break;
            case 'synthesis_start':
              callbacks.onSynthesisStart();
              break;
            case 'synthesis_text':
              callbacks.onSynthesisText(parsed.text as string);
              break;
            case 'synthesis_done':
              callbacks.onSynthesisDone((parsed.confidence as string) ?? 'MEDIUM');
              break;
            case 'usage':
              callbacks.onUsage(parsed as { inputTokens: number; outputTokens: number; cost_usd: number });
              break;
            case 'done':
              callbacks.onDone();
              return;
            case 'error':
              callbacks.onError(parsed.error as string);
              return;
          }
        } catch {
          // Non-JSON — ignore
        }
      }
    }
    callbacks.onDone();
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      callbacks.onDone();
    } else {
      callbacks.onError('Connection lost. Please try again.');
    }
  }
}

export async function fetchChecklist(checklistId: string): Promise<ChecklistData | null> {
  try {
    const res = await authFetch(`${API_BASE}/api/checklists/${encodeURIComponent(checklistId)}`);
    if (!res.ok) return null;
    const data = await res.json() as { checklist: ChecklistData };
    return data.checklist ?? null;
  } catch {
    return null;
  }
}

/* ── F2: Checklist Evidence (photo attachments) ──────────────── */

/**
 * Upload a photo as evidence for a checklist item.
 * Returns the created evidence record (with URL).
 */
export async function uploadEvidence(
  checklistId: string,
  itemId: string,
  file: File,
): Promise<ChecklistEvidence> {
  const formData = new FormData();
  formData.append('file', file, file.name);

  const res = await authFetch(
    `${API_BASE}/api/checklists/${encodeURIComponent(checklistId)}/items/${encodeURIComponent(itemId)}/evidence`,
    {
      method: 'POST',
      body: formData,
      // Note: do NOT set Content-Type — browser sets multipart boundary
    },
  );

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: 'Upload failed' })) as { error?: string };
    throw new Error(errBody.error || `Upload failed (${res.status})`);
  }

  const data = await res.json() as { evidence: ChecklistEvidence };
  // Resolve relative URL to absolute
  if (data.evidence.url && !data.evidence.url.startsWith('http')) {
    data.evidence.url = `${API_BASE}${data.evidence.url}`;
  }
  return data.evidence;
}

/**
 * Fetch all evidence records for a checklist item.
 */
export async function fetchEvidence(
  checklistId: string,
  itemId: string,
): Promise<ChecklistEvidence[]> {
  try {
    const res = await authFetch(
      `${API_BASE}/api/checklists/${encodeURIComponent(checklistId)}/items/${encodeURIComponent(itemId)}/evidence`,
    );
    if (!res.ok) return [];
    const data = await res.json() as { evidence: ChecklistEvidence[] };
    const list = data.evidence ?? [];
    // Resolve relative URLs
    return list.map((ev) => ({
      ...ev,
      url: ev.url && !ev.url.startsWith('http') ? `${API_BASE}${ev.url}` : ev.url,
    }));
  } catch {
    return [];
  }
}

/**
 * Delete a single evidence record (removes from R2 + D1).
 */
export async function deleteEvidence(
  checklistId: string,
  evidenceId: string,
): Promise<void> {
  const res = await authFetch(
    `${API_BASE}/api/checklists/${encodeURIComponent(checklistId)}/evidence/${encodeURIComponent(evidenceId)}`,
    { method: 'DELETE' },
  );
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: 'Delete failed' })) as { error?: string };
    throw new Error(errBody.error || `Delete failed (${res.status})`);
  }
}

/* ── F3: Checklist Sign / Void / Audit ────────────────────── */

/**
 * Sign and lock a checklist.
 * Returns the updated ChecklistData (with locked=true, signedBy, signedAt).
 */
export async function signChecklist(
  checklistId: string,
  signed_by: string,
  comment?: string,
): Promise<ChecklistData> {
  const res = await authFetch(
    `${API_BASE}/api/checklists/${encodeURIComponent(checklistId)}/sign`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ signed_by, comment }),
    },
  );
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: 'Sign failed' })) as { error?: string };
    throw new Error(errBody.error || `Sign failed (${res.status})`);
  }
  const data = await res.json() as { checklist: ChecklistData };
  return data.checklist;
}

/**
 * Void (unlock) a signed checklist. Admin only.
 * Returns the updated ChecklistData (with locked=false, voidReason set).
 */
export async function voidChecklist(
  checklistId: string,
  reason: string,
): Promise<ChecklistData> {
  const res = await authFetch(
    `${API_BASE}/api/checklists/${encodeURIComponent(checklistId)}/void`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    },
  );
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: 'Void failed' })) as { error?: string };
    throw new Error(errBody.error || `Void failed (${res.status})`);
  }
  const data = await res.json() as { checklist: ChecklistData };
  return data.checklist;
}

/**
 * Fetch audit log entries for a checklist.
 */
/* ── Compliance Copy Builder API ──────────────────────────────── */

import type { ManualSection } from './types';

export interface ManualGenerateCallbacks {
  onStatus: (phase: string, data: Record<string, unknown>) => void;
  onSection: (section: ManualSection) => void;
  onDone: (data: Record<string, unknown>) => void;
  onError: (message: string) => void;
  onClarificationNeeded: (questions: string[]) => void;
}

/**
 * POST to /api/manual/generate and stream the result via SSE.
 * Handles both the clarification short-circuit (JSON) and the SSE stream path.
 */
export async function streamManualGenerate(
  productType: string,
  targetRegions: string[],
  productDetails: string,
  callbacks: ManualGenerateCallbacks,
  clarificationAnswers?: Record<string, string>,
  signal?: AbortSignal,
): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/manual/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        product_type: productType,
        target_regions: targetRegions,
        product_details: productDetails || undefined,
        clarification_answers: clarificationAnswers || undefined,
      }),
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      callbacks.onDone({});
      return;
    }
    callbacks.onError('Unable to reach the server. Check your connection and try again.');
    return;
  }

  if (response.status === 401) {
    handleAuthFailure();
    callbacks.onError('Your session has expired. Please sign in again.');
    return;
  }

  if (!response.ok) {
    try {
      const errBody = await response.json() as { error?: string };
      if (errBody.error) { callbacks.onError(errBody.error); return; }
    } catch { /* ignore */ }
    callbacks.onError(`Server error (${response.status}). Please try again.`);
    return;
  }

  // Check if this is a clarification response (JSON, not SSE)
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const json = await response.json() as { needs_clarification?: boolean; questions?: string[] };
    if (json.needs_clarification && json.questions) {
      callbacks.onClarificationNeeded(json.questions);
      return;
    }
    callbacks.onError('Unexpected response from server.');
    return;
  }

  // SSE stream path
  const body = response.body;
  if (!body) { callbacks.onError('Empty response from server.'); return; }

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  if (signal) {
    signal.addEventListener('abort', () => { reader.cancel().catch(() => {}); }, { once: true });
  }

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (signal?.aborted) break;

      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() ?? '';

      for (const chunk of chunks) {
        let eventName = 'message';
        let eventData = '';
        for (const line of chunk.split('\n')) {
          if (line.startsWith('event: ')) eventName = line.slice(7).trim();
          else if (line.startsWith('data: ')) eventData += line.slice(6);
          else if (line.startsWith('data:')) eventData += line.slice(5);
        }
        if (!eventData) continue;
        try {
          const parsed = JSON.parse(eventData) as Record<string, unknown>;
          switch (eventName) {
            case 'status':
              callbacks.onStatus(parsed.phase as string, parsed);
              break;
            case 'section':
              callbacks.onSection(parsed as unknown as ManualSection);
              break;
            case 'done':
              callbacks.onDone(parsed);
              return;
            case 'error':
              callbacks.onError((parsed.message as string) || 'Generation failed.');
              return;
          }
        } catch { /* Non-JSON — ignore */ }
      }
    }
    callbacks.onDone({});
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      callbacks.onDone({});
    } else {
      callbacks.onError('Connection lost. Please try again.');
    }
  }
}

// ─── Manual Review SSE streaming ─────────────────────────────────────────────

import type { ReviewGap } from './types';

export interface ManualReviewCallbacks {
  onStatus: (phase: string, data: Record<string, unknown>) => void;
  onGap: (gap: ReviewGap) => void;
  onDone: (data: Record<string, unknown>) => void;
  onError: (message: string) => void;
  onConfirmationNeeded: (guesses: string[], summary: string) => void;
}

/**
 * POST to /api/manual/review and stream the result via SSE.
 * Accepts a PDF File + optional confirmed product type for re-submissions.
 */
export async function streamManualReview(
  pdfFile: File,
  callbacks: ManualReviewCallbacks,
  confirmedProductType?: string,
  confirmedDomains?: string[],
  signal?: AbortSignal,
): Promise<void> {
  const formData = new FormData();
  formData.append('pdf', pdfFile);
  if (confirmedProductType) {
    formData.append('confirmed_product_type', confirmedProductType);
  }
  if (confirmedDomains && confirmedDomains.length > 0) {
    formData.append('confirmed_domains', JSON.stringify(confirmedDomains));
  }

  let response: Response;
  try {
    // Build auth headers WITHOUT Content-Type (browser sets multipart boundary)
    const authHdrs = getAuthHeaders();
    response = await fetch(`${API_BASE}/api/manual/review`, {
      method: 'POST',
      headers: authHdrs,
      body: formData,
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      callbacks.onDone({});
      return;
    }
    callbacks.onError('Unable to reach the server. Check your connection and try again.');
    return;
  }

  if (response.status === 401) {
    handleAuthFailure();
    callbacks.onError('Your session has expired. Please sign in again.');
    return;
  }
  if (response.status === 413) {
    callbacks.onError('PDF file is too large. Maximum size is 10MB.');
    return;
  }
  if (!response.ok) {
    try {
      const errBody = await response.json() as { error?: string };
      if (errBody.error) { callbacks.onError(errBody.error); return; }
    } catch { /* ignore */ }
    callbacks.onError(`Server error (${response.status}). Please try again.`);
    return;
  }

  const body = response.body;
  if (!body) { callbacks.onError('Empty response from server.'); return; }

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  if (signal) {
    signal.addEventListener('abort', () => { reader.cancel().catch(() => {}); }, { once: true });
  }

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (signal?.aborted) break;

      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() ?? '';

      for (const chunk of chunks) {
        let eventName = 'message';
        let eventData = '';
        for (const line of chunk.split('\n')) {
          if (line.startsWith('event: ')) eventName = line.slice(7).trim();
          else if (line.startsWith('data: ')) eventData += line.slice(6);
          else if (line.startsWith('data:')) eventData += line.slice(5);
        }
        if (!eventData) continue;
        try {
          const parsed = JSON.parse(eventData) as Record<string, unknown>;
          switch (eventName) {
            case 'status':
              callbacks.onStatus(parsed.phase as string, parsed);
              break;
            case 'gap':
              callbacks.onGap(parsed as unknown as ReviewGap);
              break;
            case 'confirmation_needed':
              callbacks.onConfirmationNeeded(
                (parsed.guesses as string[]) || [],
                (parsed.manual_summary as string) || '',
              );
              return;
            case 'done':
              callbacks.onDone(parsed);
              return;
            case 'error':
              callbacks.onError((parsed.message as string) || 'Review failed.');
              return;
            default:
              break;
          }
        } catch { /* Non-JSON chunk, skip */ }
      }
    }
    callbacks.onDone({});
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      callbacks.onDone({});
    } else {
      callbacks.onError('Connection lost. Please try again.');
    }
  }
}

/* ── Query Template Library ──────────────────────────────────────────────────── */

export interface QueryTemplate {
  id: string;
  name: string;
  query: string;
  created_at: number;
}

export async function fetchTemplates(): Promise<QueryTemplate[]> {
  try {
    const res = await authFetch(`${API_BASE}/api/templates`);
    if (!res.ok) return [];
    const data = await res.json() as { templates: QueryTemplate[] };
    return data.templates ?? [];
  } catch {
    return [];
  }
}

export async function createTemplate(name: string, query: string): Promise<QueryTemplate> {
  const res = await authFetch(`${API_BASE}/api/templates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, query }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Create failed' })) as { error?: string };
    throw new Error(err.error || `Create template failed (${res.status})`);
  }
  return res.json() as Promise<QueryTemplate>;
}

export async function deleteTemplate(id: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/api/templates/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Delete failed' })) as { error?: string };
    throw new Error(err.error || `Delete template failed (${res.status})`);
  }
}

/* ── User Me / Cost Indicator ──────────────────────────────────────── */

export interface UserMeData {
  authenticated: boolean;
  user_id: string;
  email: string | null;
  name: string | null;
  role: string;
  auth_method: string;
  /** Today’s spend in USD (combined from /api/user/spend) */
  user_today_spend_usd: number;
  /** Daily cap in USD (worker env DAILY_SPEND_CAP_USD, default 100) */
  user_daily_cap_usd: number;
}

export async function fetchUserMe(): Promise<UserMeData | null> {
  try {
    const [meRes, spendRes] = await Promise.all([
      authFetch(`${API_BASE}/api/user/me`),
      authFetch(`${API_BASE}/api/user/spend`),
    ]);
    if (!meRes.ok) return null;
    const me = await meRes.json() as {
      authenticated: boolean; user_id: string; email: string | null;
      name: string | null; role: string; auth_method: string;
    };
    const spend = spendRes.ok
      ? await spendRes.json() as { today: number; user_daily_cap_usd?: number }
      : { today: 0 };
    return {
      ...me,
      user_today_spend_usd: spend.today ?? 0,
      user_daily_cap_usd: spend.user_daily_cap_usd ?? 100,
    };
  } catch {
    return null;
  }
}

export async function fetchChecklistAudit(
  checklistId: string,
): Promise<ChecklistAuditEntry[]> {
  try {
    const res = await authFetch(
      `${API_BASE}/api/checklists/${encodeURIComponent(checklistId)}/audit`,
    );
    if (!res.ok) return [];
    const data = await res.json() as { audit: ChecklistAuditEntry[] };
    return data.audit ?? [];
  } catch {
    return [];
  }
}
